// Home v2 — holdings table. Real portfolio/railgun tokens (see useHomeDashboard).
// Toolbar reuses the previous-version controls: the network filter (SelectNetwork),
// an asset-type dropdown (Tokens/NFTs/DeFi) and the Search input. `bodyOverride`
// lets the screen swap in the existing Collections / DeFiPositions lists.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type Control, useWatch } from 'react-hook-form'
import { Pressable, View } from 'react-native'

import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import PureSpinner from '@common/assets/svg/PureSpinner'
import SearchIcon from '@common/assets/svg/SearchIcon'
import Search from '@common/components/Search'
import TokenIcon from '@common/components/TokenIcon'
import RollingText from '@common/components/RollingText'
import Text from '@common/components/Text'
import TokensSkeleton from '@common/modules/dashboard/components/Tokens/TokensSkeleton'
import useTheme from '@common/hooks/useTheme'
import { TabType } from '@common/modules/dashboard/components/TabsAndSearch/Tabs/Tab/Tab'
import { Mode } from '@web/contexts/appModeContext'

import { AssetRowData } from '../useHomeDashboard'
import useHomeBreakpoints from '../useHomeBreakpoints'
import { getDeltaColor, getModeAccent, getModeAccentSoft, RADII } from '../tokens'
import AssetTypeDropdown from './AssetTypeDropdown'
import NetworkPill from './NetworkPill'
import { PlusIcon } from './HomeIcons'

type Props = {
  assets: AssetRowData[]
  /** Dust/spam rows (no USD price or < $0.01) collapsed behind the
   * "N other tokens" summary row. See dustTokens.ts. */
  dustAssets?: AssetRowData[]
  dustTotalUSD?: number
  isLoading: boolean
  mode: Mode
  selectedId: string | null
  onSelect: (id: string) => void
  onAddToken: () => void
  assetType: TabType
  onAssetTypeChange: (tab: TabType) => void
  searchControl: Control<{ search: string }, any>
  onSearchCleared: () => void
  /** When set (NFTs / DeFi tabs), replaces the token rows with this content. */
  bodyOverride?: React.ReactNode
  /** Private balance fetch failed (Railgun engine/DB error). Shows a retry
   * prompt instead of the misleading "no tokens" empty state. */
  privateError?: boolean
  /** Private cold-sync: scan finished but POI verification still running and
   * funds are pending-POI. Shows a "verifying" hint instead of "no tokens". */
  pendingVerification?: boolean
  onRetry?: () => void
  /** Name of the active network filter, if any. Turns the empty state into
   * "No assets on <Network>." so a filtered-but-non-empty wallet doesn't read
   * as an empty wallet. */
  networkFilterName?: string | null
}

// Rows rendered on first paint. A whale address (or any account holding airdrop
// dust across every chain) can return 1000+ tokens, and each row mounts a
// TokenIcon plus two RollingText instances — i.e. ~12 Reanimated cells. Mounting
// all of them at once is what pins the main thread and blows past the per-tab
// memory ceiling on iOS Safari. The list is inside AppShell's page-level
// ScrollView, so a nested FlatList would fight that scroll container; we window
// it manually instead and grow the window as the user reaches the end.
const INITIAL_VISIBLE_ROWS = 25
const VISIBLE_ROWS_STEP = 25

const AssetRow = ({
  asset,
  selected,
  accent,
  accentSoft,
  onSelect
}: {
  asset: AssetRowData
  selected: boolean
  accent: string
  accentSoft: string
  onSelect: (id: string) => void
}) => {
  const { theme } = useTheme()
  const [hovered, setHovered] = useState(false)
  const isUp = (asset.deltaPct ?? 0) >= 0

  return (
    <Pressable
      onPress={() => onSelect(asset.id)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: RADII.sm,
        borderLeftWidth: selected ? 3 : 0,
        borderLeftColor: accent,
        backgroundColor: selected
          ? accentSoft
          : hovered
            ? (theme.quaternaryBackground as string)
            : 'transparent'
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
        <TokenIcon
          address={asset.address}
          chainId={asset.chainId}
          width={36}
          height={36}
          withNetworkIcon
        />
        <View style={{ minWidth: 0 }}>
          <Text fontSize={14} weight="semiBold" numberOfLines={1}>
            {asset.symbol}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text fontSize={14} appearance="secondaryText" numberOfLines={1}>
              {asset.priceUSDFormatted}
            </Text>
            {asset.deltaPct != null && (
              <Text fontSize={14} weight="medium" color={getDeltaColor(theme, isUp)}>
                {isUp ? '+' : ''}
                {asset.deltaPct.toFixed(2)}%
              </Text>
            )}
          </View>
        </View>
      </View>
      <View style={{ alignItems: 'flex-end', marginLeft: 16 }}>
        <RollingText fontSize={14} weight="semiBold" value={asset.balanceFormatted} />
        <RollingText fontSize={14} appearance="secondaryText" value={asset.balanceUSDFormatted} />
        {asset.pendingPOIUsd > 0 && (
          <Text fontSize={11} weight="medium" color={accent} numberOfLines={1}>
            {`+${formatDecimals(asset.pendingPOIUsd, 'value')} pending`}
          </Text>
        )}
      </View>
    </Pressable>
  )
}

// Memoized so growing the window (or any parent re-render) re-renders only the
// newly added rows instead of every already-mounted one.
const MemoAssetRow = React.memo(AssetRow)

const AssetTable: React.FC<Props> = ({
  assets,
  dustAssets = [],
  dustTotalUSD = 0,
  isLoading,
  mode,
  selectedId,
  onSelect,
  onAddToken,
  assetType,
  onAssetTypeChange,
  searchControl,
  onSearchCleared,
  bodyOverride,
  privateError,
  pendingVerification,
  onRetry,
  networkFilterName
}) => {
  const { theme } = useTheme()
  const { isMobile } = useHomeBreakpoints()
  const accent = getModeAccent(theme, mode)
  const accentSoft = getModeAccentSoft(theme, mode)

  const [isSearchVisible, setIsSearchVisible] = useState(false)
  const searchRef = useRef<any>(null)
  const searchButtonRef = useRef<any>(null)
  // Live search term — lets the empty state distinguish "no match" from
  // "no tokens at all" (and anchors the e2e search test).
  const searchTerm = (useWatch({ control: searchControl, name: 'search' }) || '').trim()

  const [visibleRows, setVisibleRows] = useState(INITIAL_VISIBLE_ROWS)
  const [isDustExpanded, setIsDustExpanded] = useState(false)

  // A new search / tab / mode is a new list — start the window over so the user
  // doesn't land deep inside a result set they just narrowed down.
  useEffect(() => {
    setVisibleRows(INITIAL_VISIBLE_ROWS)
    setIsDustExpanded(false)
  }, [searchTerm, assetType, mode])

  // Expanded dust rows join the same manual window so a spam-heavy account
  // can't mount hundreds of rows at once (see INITIAL_VISIBLE_ROWS note).
  const listAssets = useMemo(
    () => (isDustExpanded ? [...assets, ...dustAssets] : assets),
    [assets, dustAssets, isDustExpanded]
  )
  const visibleAssets = useMemo(() => listAssets.slice(0, visibleRows), [listAssets, visibleRows])
  const remainingRows = listAssets.length - visibleAssets.length

  const showMoreRows = useCallback(() => setVisibleRows((count) => count + VISIBLE_ROWS_STEP), [])

  // Close the search when clicking outside it (mirrors TabsAndSearch).
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const onButton = searchButtonRef.current?.contains?.(e.target as Node)
      const onSearch = searchRef.current?.contains?.(e.target as Node)
      if (!isSearchVisible || onButton || onSearch) return
      setIsSearchVisible(false)
    }
    window.addEventListener('mousedown', onClick, { passive: true })
    return () => window.removeEventListener('mousedown', onClick)
  }, [isSearchVisible])

  const showTokenBody = !bodyOverride

  return (
    <View
      style={{
        backgroundColor: theme.homeCardBackground as string,
        borderRadius: RADII.lg,
        padding: 16
      }}
    >
      {/* toolbar */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 16,
          zIndex: 20
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            zIndex: 20
          }}
        >
          <NetworkPill currentTab={assetType} />
          <AssetTypeDropdown value={assetType} onChange={onAssetTypeChange} compact={isMobile} />
          <Pressable
            ref={searchButtonRef}
            testID={`search-glass-icon-${assetType}`}
            onPress={() => setIsSearchVisible((v) => !v)}
            style={{
              width: isMobile ? 30 : 36,
              height: isMobile ? 30 : 36,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: RADII.pill,
              backgroundColor: isSearchVisible
                ? (theme.infoBackground as string)
                : (theme.homeControlBackground as string),
              borderWidth: 1,
              borderColor: (isSearchVisible ? theme.primary : theme.homeCardBorder) as string
            }}
          >
            <SearchIcon
              width={isMobile ? 14 : 18}
              height={isMobile ? 14 : 18}
              color={(isSearchVisible ? theme.primary : theme.secondaryText) as string}
            />
          </Pressable>
        </View>
        <Pressable
          onPress={onAddToken}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
        >
          <PlusIcon size={16} color={theme.linkText as string} />
          <Text fontSize={14} weight="semiBold" color={theme.linkText as string}>
            Add token
          </Text>
        </Pressable>
      </View>

      {/* search row — full-width below the toolbar when toggled open */}
      {isSearchVisible && (
        <View ref={searchRef} style={{ marginBottom: 16, zIndex: 19 }}>
          <Search
            autoFocus
            control={searchControl}
            height={36}
            hasLeftIcon={false}
            placeholder="Search for tokens"
            onSearchCleared={() => {
              onSearchCleared()
              setIsSearchVisible(false)
            }}
          />
        </View>
      )}

      {/* body */}
      {showTokenBody ? (
        <>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              paddingHorizontal: 12,
              marginBottom: 8
            }}
          >
            <Text fontSize={12} appearance="secondaryText">
              Total assets
            </Text>
            <Text fontSize={12} appearance="secondaryText">
              Amount/USD
            </Text>
          </View>
          {privateError && assets.length === 0 ? (
            <View style={{ paddingVertical: 24, alignItems: 'center', gap: 8 }}>
              <Text fontSize={14} weight="medium" style={{ textAlign: 'center' }}>
                Couldn't load your private balance.
              </Text>
              <Text
                fontSize={13}
                appearance="secondaryText"
                style={{ textAlign: 'center', maxWidth: 320 }}
              >
                The private engine failed to start. Retry, or reload the page if it keeps failing.
              </Text>
              {onRetry && (
                <Pressable
                  onPress={onRetry}
                  style={{
                    marginTop: 4,
                    paddingVertical: 8,
                    paddingHorizontal: 20,
                    borderRadius: RADII.pill,
                    backgroundColor: accentSoft,
                    borderWidth: 1,
                    borderColor: accent
                  }}
                >
                  <Text fontSize={14} weight="semiBold" color={accent}>
                    Retry
                  </Text>
                </Pressable>
              )}
            </View>
          ) : isLoading && assets.length === 0 && !searchTerm ? (
            <TokensSkeleton amount={4} withHeader={false} />
          ) : pendingVerification ? (
            <View style={{ paddingVertical: 24, alignItems: 'center', gap: 10 }}>
              <PureSpinner style={{ width: 22, height: 22 }} color={accent} />
              <Text fontSize={14} weight="medium" style={{ textAlign: 'center' }}>
                Verifying your private balance…
              </Text>
              <Text
                fontSize={13}
                appearance="secondaryText"
                style={{ textAlign: 'center', maxWidth: 320 }}
              >
                Incoming funds appear here once proof-of-innocence verification completes.
              </Text>
            </View>
          ) : assets.length === 0 && dustAssets.length === 0 ? (
            <View style={{ paddingVertical: 24, alignItems: 'center' }}>
              <Text testID="no-tokens-text" fontSize={14} weight="medium">
                {/* eslint-disable-next-line no-nested-ternary */}
                {searchTerm
                  ? `No tokens match "${searchTerm}".`
                  : networkFilterName
                    ? `No assets on ${networkFilterName}.`
                    : "You don't have any tokens yet."}
              </Text>
            </View>
          ) : (
            <>
              {visibleAssets.map((asset) => (
                <MemoAssetRow
                  key={asset.id}
                  asset={asset}
                  selected={asset.id === selectedId}
                  accent={accent}
                  accentSoft={accentSoft}
                  onSelect={onSelect}
                />
              ))}
              {remainingRows > 0 && (
                <Pressable
                  testID="show-more-tokens"
                  onPress={showMoreRows}
                  style={{
                    marginTop: 8,
                    paddingVertical: 10,
                    alignItems: 'center',
                    borderRadius: RADII.sm,
                    backgroundColor: theme.quaternaryBackground as string
                  }}
                >
                  <Text fontSize={13} weight="medium" color={accent}>
                    {`Show ${Math.min(remainingRows, VISIBLE_ROWS_STEP)} more (${remainingRows} left)`}
                  </Text>
                </Pressable>
              )}
              {dustAssets.length > 0 && (
                <Pressable
                  testID="other-tokens-toggle"
                  onPress={() => setIsDustExpanded((v) => !v)}
                  style={{
                    marginTop: 8,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingVertical: 10,
                    paddingHorizontal: 12,
                    borderRadius: RADII.sm,
                    backgroundColor: theme.quaternaryBackground as string
                  }}
                >
                  <Text fontSize={13} weight="medium" appearance="secondaryText">
                    {isDustExpanded
                      ? `Hide ${dustAssets.length} other token${dustAssets.length > 1 ? 's' : ''}`
                      : `${dustAssets.length} other token${dustAssets.length > 1 ? 's' : ''}`}
                  </Text>
                  {!isDustExpanded && (
                    <Text fontSize={13} weight="medium" appearance="secondaryText">
                      {formatDecimals(dustTotalUSD, 'value')}
                    </Text>
                  )}
                </Pressable>
              )}
            </>
          )}
        </>
      ) : (
        bodyOverride
      )}
    </View>
  )
}

export default React.memo(AssetTable)
